import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Perfil from './components/Perfil';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Farmacia from './components/Farmacia';
import Productos from './components/Productos';
import Almacen from './components/Almacen';
import AlmacenConsumo from './components/AlmacenConsumo';
import ProtectedRoute from './components/ProtectedRoute';
import { authService, startSessionHeartbeat } from './services/api';
import Indicadores from './components/Indicadores';
import ReporteVentas from './components/ReporteVentas';
import ConsumoPaciente from './components/ConsumoPaciente';
import Economia from './components/Economia';
import ComprobantesPago from './components/ComprobantesPago';
import LibroVentas from './components/LibroVentas';
import MonitorEnvios from './components/MonitorEnvios';
import Requerimiento from './components/Requerimiento';
import LibroPsicotropicos from './components/LibroPsicotropicos';
import LibroNarcoticos from './components/LibroNarcoticos';
import CambioPasswordObligatorio from './components/CambioPasswordObligatorio';
import DevolucionDosis from './components/DevolucionDosis';
import NotFound from './components/NotFound';
import EnConstruccion from './components/EnConstruccion';
import ReporteConteo from './components/ReporteConteo';
import CotizarCertificado from './components/CotizarCertificado';
import ConfiguracionEconomia from './components/ConfiguracionEconomia';
import Usuarios from './components/Usuarios';
import Roles from './components/Roles';
import Caja from './components/Caja';
import Anulaciones from './components/Anulaciones';
import SesionesCaja from './components/SesionesCaja';
import Catalogos from './components/Catalogos';
import Precios from './components/Precios';
import Ventas from './components/Ventas';
import ConfiguracionCajas from './components/ConfiguracionCajas';
import CorteCaja from './components/CorteCaja';
import Datafarma from './components/Datafarma';
import VentasDatafarma from './components/VentasDatafarma';
import CorteCajaFarmacia from './components/CorteCajaDatafarma';
import Seguros from './components/Seguros';
import KardexFarmacia from './components/KardexFarmacia';
import Reportes from './components/Reportes';
import CuentasPorCobrar from './components/CuentasPorCobrar';
import ConsultaComprobantesPaciente from './components/ConsultaComprobantesPaciente';
import EnvioMasivoSunat from './components/EnvioMasivoSunat';
import TableroEconomia from './components/TableroEconomia';

// ⭐ TEMA MORADO INTEGRA
const theme = createTheme({
  palette: {
    primary: {
      main: '#6610F5',
      dark: '#5010C5',
      light: '#8640F7',
    },
    secondary: {
      main: '#DC143C',
      dark: '#B01030',
      light: '#E63D5E',
    },
    background: {
      default: '#F8F9FA',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Segoe UI',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

function LogoutNavigation() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const leave = () => queueMicrotask(() => navigate('/login', {replace:true}));
    window.addEventListener('integra:logout', leave);
    return () => window.removeEventListener('integra:logout', leave);
  }, [navigate]);
  return null;
}

function PasswordGate({children}) {
  const location = useLocation();
  if(authService.isAuthenticated() && localStorage.getItem('password_change_required')==='1' && location.pathname!=='/cambio-password-obligatorio')
    return <Navigate to="/cambio-password-obligatorio" replace />;
  return children;
}

function App() {
  React.useEffect(()=>startSessionHeartbeat(),[]);
  const isAuthenticated = authService.isAuthenticated();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <LogoutNavigation />
        <PasswordGate>
        <Routes>
          <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
          {/* Ruta raíz */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Login */}
          <Route path="/login" element={<Login />} />

          {/* Dashboard sin Layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Módulo Farmacia sin Layout */}
          <Route
            path="/farmacia"
            element={
              <ProtectedRoute>
                <Farmacia />
              </ProtectedRoute>
            }
          />

          {/* Farmacia - Productos (Stock) */}
          <Route
            path="/farmacia/productos"
            element={
              <ProtectedRoute>
                <Productos />
              </ProtectedRoute>
            }
          />

           {/* Farmacia - Almacén */}
          <Route
            path="/farmacia/almacen"
            element={
              <ProtectedRoute>
                <Almacen />
              </ProtectedRoute>
            }
          />
          <Route path="/seguros" element={<Seguros/>}/>
          <Route path="/farmacia/consumo" element={<AlmacenConsumo />} />
          <Route path="/farmacia/indicadores" element={<Indicadores />} />

          <Route path="/farmacia/reporte-ventas" element={<ReporteVentas />} />

          <Route path="/seguros/consumo-paciente"  element={<ConsumoPaciente modulo="seguros"  />} />
          <Route path="/farmacia/consumo-paciente" element={<ConsumoPaciente modulo="farmacia" />} />
          

          <Route path="/economia" element={<ProtectedRoute><Economia /></ProtectedRoute>} />
          <Route path="/economia/comprobantes-pago" element={<ProtectedRoute><ComprobantesPago modulo="economia" /></ProtectedRoute>} />

          <Route path="/economia/libro-ventas" element={<ProtectedRoute><LibroVentas modulo="economia" /></ProtectedRoute>} />
          <Route path="/economia/monitor-envios" element={<ProtectedRoute><MonitorEnvios modulo="economia" /></ProtectedRoute>} />

          <Route path="/cambio-password-obligatorio" element={<ProtectedRoute><CambioPasswordObligatorio /></ProtectedRoute>} />
          <Route path="/farmacia/libro-psicotropicos" element={<ProtectedRoute><LibroPsicotropicos /></ProtectedRoute>} />
          <Route path="/farmacia/libro-narcoticos" element={<ProtectedRoute><LibroNarcoticos /></ProtectedRoute>} />
          <Route path="/farmacia/devoluciones-dosis" element={<ProtectedRoute><DevolucionDosis /></ProtectedRoute>} />

          <Route path="*" element={<NotFound />} />
          <Route path="/en-construccion" element={<EnConstruccion />} />
          <Route path="/admision" element={<EnConstruccion />} />
          <Route path="/farmacia/reporte-conteo" element={<ProtectedRoute><ReporteConteo /></ProtectedRoute>} />
          <Route path="/economia/cotizar-certificado" element={<ProtectedRoute><CotizarCertificado /></ProtectedRoute>} />
          <Route path="/economia/configuracion" element={<ProtectedRoute><ConfiguracionEconomia /></ProtectedRoute>} />
          <Route path="/usuarios" element={<ProtectedRoute><Usuarios /></ProtectedRoute>} />
          <Route path="/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />
          <Route path="/farmacia/requerimiento"element={<ProtectedRoute><Requerimiento /></ProtectedRoute>}/>
          <Route path="/caja" element={<ProtectedRoute><Caja /></ProtectedRoute>} />
          <Route path="/caja/anulaciones" element={<ProtectedRoute><Anulaciones dependencia="CAJA" /></ProtectedRoute>} />

          
          <Route path="/caja/sesiones" element={<ProtectedRoute><SesionesCaja /></ProtectedRoute>} />
          <Route path="/caja/procedimientos" element={<ProtectedRoute><Catalogos /></ProtectedRoute>} />
          <Route path="/caja/tarifario" element={<ProtectedRoute><Precios /></ProtectedRoute>} />
          <Route path="/caja/ajustes" element={<ProtectedRoute><ConfiguracionCajas modulo="CAJA" /></ProtectedRoute>} />
          <Route path="/datafarma/ajustes" element={<ProtectedRoute><ConfiguracionCajas modulo="DATAFARMA" /></ProtectedRoute>} />
          <Route path="/caja/corte" element={<ProtectedRoute><CorteCaja /></ProtectedRoute>} />

          <Route path="/datafarma/ventas" element={<ProtectedRoute><VentasDatafarma /></ProtectedRoute>} />
          <Route path="/datafarma" element={<ProtectedRoute><Datafarma /></ProtectedRoute>} />
          <Route path="/datafarma/anulaciones" element={<ProtectedRoute><Anulaciones dependencia="DATAFARMA" /></ProtectedRoute>} />
          <Route path="/datafarma/corte" element={<ProtectedRoute><CorteCajaFarmacia /></ProtectedRoute>} />
          
          
          <Route path="/caja/ventas" element={<ProtectedRoute><Ventas /></ProtectedRoute>} />

          <Route path="/seguros/kardexfarmacia" element={<KardexFarmacia modulo="seguros"/>}/>
          
          <Route path="/caja/reportes" element={<Reportes/>} />

          <Route path="/economia/envio-masivo-sunat" element={<EnvioMasivoSunat />} />
          <Route path="/economia/tablero" element={<ProtectedRoute><TableroEconomia modulo="economia" /></ProtectedRoute>} />


          <Route
            path="/economia/cuentas-por-cobrar"
            element={
              <ProtectedRoute>
                <CuentasPorCobrar />
              </ProtectedRoute>
            }
          />

          <Route
            path="/economia/consulta-cpe"
            element={
              <ProtectedRoute>
                <ConsultaComprobantesPaciente />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PasswordGate>
      </Router>
    </ThemeProvider>
  );
}

export default App;
